import React, { useState } from 'react';
import { Button, CircularProgress, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useDossierDocumentsCtx } from '@europrocurement/l2d-modules/providers';
import { useDossierDocumentFilesApi } from '../../../hooks';
import { useCurrentDossierCtx } from '../../../providers';

const DownloadRequiredDocumentsButton: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const { downloadDosDocFilesAsArchive } = useDossierDocumentFilesApi();
    const { palette } = useTheme();
    const [processing, setProcessing] = useState<boolean>(false);
    const { getDossierDocumentsByCategory } = useDossierDocumentsCtx();

    const countUploaded = [
        ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED }),
        ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED, additionalDocs: true }),
    ].reduce((accumulator, doc) => (doc.fichierId ? accumulator + 1 : accumulator), 0);

    if (!countUploaded) {
        return null;
    }

    const handleDownloadFiles = async () => {
        if (!dossier) {
            return;
        }
        setProcessing(true);

        try {
            await downloadDosDocFilesAsArchive(`${dossier.numero}.zip`, dossier.id as number, [
                DOC_TYPE_REQUIRED,
            ]);
        } catch (e) {
            console.error(e);

            enqueueSnackbar('Une erreur est survenue lors du téléchargement de vos documents', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const buttonSx = {
        display: 'flex',
        columnGap: '10px',
        paddingX: '1em',
        color: palette.primary.contrastText,
        backgroundColor: palette.primary.main,
        ':hover': {
            backgroundColor: palette.primary.light,
        },
        ':disabled': {
            backgroundColor: palette.grey[100],
        },
    };

    const circularSx = {
        color: palette.grey[400],
    };

    return (
        <Button
            data-testid="download-required-documents-button"
            onClick={handleDownloadFiles}
            sx={buttonSx}
            disabled={processing}
        >
            {processing && (
                <CircularProgress
                    sx={circularSx}
                    size="16px"
                />
            )}
            {!processing && <FaOptionIcon icon={['fas', 'file-download']} />}
        </Button>
    );
};

export default DownloadRequiredDocumentsButton;
